import { useActiveMenuElements } from '@/composables/menu'
import { useRoute } from '@/composables/route'
import {
  AIRSTAGE_SITES_SUB_PAGES,
  useCurrentSectionMenu,
  useSectionPages,
} from '@/composables/sectionTabs'
import { usePage } from '@inertiajs/vue3'
import { computed, onBeforeUnmount, ref, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * The end of the trail for pages the layout cannot place on its own (the user settings, the
 * events overview), registered by the page itself for as long as it is mounted.
 */
const breadcrumbTail = ref([])

/**
 * Registers the calling component's end of the trail; it follows reactive labels and is removed
 * when the component unmounts.
 *
 * @param {() => Array<{label: string, href?: string|null}>} items
 */
export const useBreadcrumbTail = (items) => {
  watchEffect(() => {
    breadcrumbTail.value = items()
  })
  onBeforeUnmount(() => {
    breadcrumbTail.value = []
  })
}

/**
 * The design-system look's breadcrumb trail: account (to the user settings) / event (to its
 * cockpit) / section (its first page) / current page. Event pages outside a section end with
 * their sidebar entry; organisation pages run account / section / page when they belong to one
 * of its sections, then what they registered (useBreadcrumbTail). The last item has no link.
 *
 * @returns {import('vue').ComputedRef<Array<{key: string, label: string, href: string|null}>>}
 */
export const useBreadcrumbs = () => {
  const { t } = useI18n()
  const pages = useSectionPages()

  const appendTail = (trail) =>
    breadcrumbTail.value.forEach((item, index) =>
      trail.push({
        key: `tail-${index}`,
        label: item.label,
        href: item.href ?? null,
      }),
    )

  return computed(() => {
    const page = usePage()
    const company = page.props._user?.active_company
    const event = page.props.event
    const trail = []
    let pageListHref = null

    if (company?.name) {
      trail.push({
        key: 'account',
        label: company.name,
        href: useRoute('account.settings.show'),
      })
    }

    const pushSection = (section) => {
      const first = pages.value.find((candidate) => !candidate.locked)
      const active = pages.value.find((candidate) => candidate.active)

      trail.push({
        key: 'section',
        label: t(`common.${section.label}`),
        href: first?.href ?? null,
      })

      if (active) {
        trail.push({
          key: 'page',
          label: t(active.labelKey),
          href: breadcrumbTail.value.length > 0 ? active.href : null,
        })
      }
    }

    if (event?.id) {
      trail.push({
        key: 'event',
        label: event.name,
        href: useRoute('events.cockpit', { event: event.id }),
      })

      const section = useCurrentSectionMenu()

      if (section !== null) {
        const first = pages.value.find((candidate) => !candidate.locked)
        const active = pages.value.find((candidate) => candidate.active)

        trail.push({
          key: 'section',
          label: t(`common.${section.label}`),
          href: first?.href ?? null,
        })

        if (active) {
          trail.push({
            key: 'page',
            label: t(`common.${active.label}`),
            href: null,
          })

          // A sub-page of the entry (a text's editor under Inhalte › Texte) links back to it.
          if (active.name !== page.component) {
            pageListHref = active.href
          }
        }
      } else {
        // Forms and Domains are pages of the Sites entry. Other components keep an exact
        // menu-name match, including ones the section host map sends somewhere else.
        const menuComponent = AIRSTAGE_SITES_SUB_PAGES.includes(page.component)
          ? 'Airstage/Sites'
          : page.component
        const entry = useActiveMenuElements().find(
          (menu) => menu.name === menuComponent,
        )

        if (entry) {
          trail.push({
            key: 'page',
            label: t(`common.${entry.label}`),
            href: null,
          })
        }
      }
    } else {
      // The organisation level's sections (Inhalte / Medien, Texte) as an event's sections; a
      // record opened from one of their pages follows, with its page linking back to the list.
      const section = useCurrentSectionMenu()

      if (section !== null) {
        pushSection(section)
      }

      appendTail(trail)
    }

    // An event page showing a record of its own (a guest opened from the guest list, a text
    // opened from Inhalte › Texte) adds it: the page itself then links back to its list - the
    // section page's own link where the record has a page of its own.
    if (event?.id && breadcrumbTail.value.length > 0) {
      const last = trail.at(-1)
      if (last?.key === 'page') {
        last.href = pageListHref ?? usePage().url.split('?')[0]
      }
      appendTail(trail)
    }

    if (trail.length > 0) {
      trail[trail.length - 1] = { ...trail[trail.length - 1], href: null }
    }

    return trail
  })
}
